import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlConfirmNewIndexAssembly, urlNewIndexPartialModels } from '../../../Constants';
import { IAction, SliceProps, SlicePropsWithData } from '../../store';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectNewAssemblyIndexState = {
    data: null,
    loading: false,
    error: null,
    isDialogOpen: false,
    confirmNewIndexPartialModels: {
        data: null,
        loading: false,
        error: null,
    },
    selectedAssemblyId: null,
    newIndexAssemblySuccess: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    openNewAssemblyIndexDialog(state: IProjectNewAssemblyIndexState) {
        state.isDialogOpen = true;
    },
    closeNewAssemblyIndexDialog(state: IProjectNewAssemblyIndexState) {
        state.isDialogOpen = false;
    },
    setSelectedAssemblyId(state: IProjectNewAssemblyIndexState, action: IAction<string>) {
        state.selectedAssemblyId = action.payload;
    },
    resetNewIndexAssemblySuccess(state: IProjectNewAssemblyIndexState) {
        state.newIndexAssemblySuccess = null;
    },
    clearNewIndexPartialModels(state: IProjectNewAssemblyIndexState) {
        state.data = null;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches all and linked partial models which also can contain the next index.
     * @param config - Object that contains the project database's replicaId and assembly id.
     * @returns Promise for the HTTP request.
     */
    getNewAssemblyIndexPartialModels: async (config: {
        replicaId: string;
        universalId: string;
    }): Promise<INewIndexAssembly> => {
        try {
            const { replicaId, universalId } = config;

            const formData = new FormData();
            formData.append('universalId', universalId);

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                },
                body: formData,
            };

            const response = await apiFetch(urlNewIndexPartialModels, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that creates a new index for the given assembly.
     * @param config - Object that contains the project database's replicaId, document subject, partial model ids and assembly subject.
     * @returns Promise for the HTTP request.
     */
    confirmNewAssemblyIndex: async (config: {
        replicaId: string;
        universalId: string;
        partialModelIds: string[];
        assemblySubject: string;
    }): Promise<string> => {
        try {
            const { replicaId, universalId, partialModelIds, assemblySubject } = config;

            const formData = new FormData();
            formData.append('universalId', universalId);
            formData.append('selectedPartialModelIds', partialModelIds.join(','));
            formData.append('assemblySubject', assemblySubject);

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                },
                body: formData,
            };

            const response = await apiFetch(urlConfirmNewIndexAssembly, request);

            return response.ok
                ? Promise.resolve(await response.text())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectNewAssemblyIndexState>) => {
        /**
         * Reducers for the 'getNewAssemblyIndexPartialModels' thunk.
         */
        builder.addCase(
            getNewAssemblyIndexPartialModels.pending,
            (state: Draft<IProjectNewAssemblyIndexState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getNewAssemblyIndexPartialModels.fulfilled,
            (state: Draft<IProjectNewAssemblyIndexState>, action: IAction<INewIndexAssembly>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getNewAssemblyIndexPartialModels.rejected,
            (state: Draft<IProjectNewAssemblyIndexState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'confirmNewAssemblyIndex' thunk.
         */
        builder.addCase(
            confirmNewAssemblyIndex.pending,
            (state: Draft<IProjectNewAssemblyIndexState>) => {
                state.confirmNewIndexPartialModels.loading = true;
                state.confirmNewIndexPartialModels.error = null;
            },
        );
        builder.addCase(
            confirmNewAssemblyIndex.fulfilled,
            (state: Draft<IProjectNewAssemblyIndexState>, action: IAction<string>) => {
                state.confirmNewIndexPartialModels.loading = false;
                state.confirmNewIndexPartialModels.error = null;
                state.confirmNewIndexPartialModels.data = action.payload;
                state.newIndexAssemblySuccess = true;
            },
        );
        builder.addCase(
            confirmNewAssemblyIndex.rejected,
            (state: Draft<IProjectNewAssemblyIndexState>, action: IAction<SerializedError>) => {
                state.confirmNewIndexPartialModels.loading = false;
                state.confirmNewIndexPartialModels.error = action.error;
                state.newIndexAssemblySuccess = false;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getNewAssemblyIndexPartialModels = createAsyncThunk(
    `${urlNewIndexPartialModels}`,
    thunks.getNewAssemblyIndexPartialModels,
);

export const confirmNewAssemblyIndex = createAsyncThunk(
    `${urlConfirmNewIndexAssembly}`,
    thunks.confirmNewAssemblyIndex,
);

export interface INewIndexAssembly extends IAssemblyDocumentProps {
    /** Document NextIndex. */
    nextIndex: string;

    /** Assembly partial models. */
    partialModels: INewPartialModel[];
}

export interface INewPartialModel extends IAssemblyDocumentProps {
    /** Is partial model aready linked to the assembly. */
    isLinked: string;

    /** New index of the new partial model. */
    newIndex: string;
}

interface IAssemblyDocumentProps {
    /** Document Subject. */
    subject: string;

    /** Document UniversalId. */
    universalId: string;

    /** Document DocType. */
    docType: string;
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectNewAssemblyIndexState extends SliceProps {
    /** Assembly document data. */
    data: INewIndexAssembly;

    /** Boolean value if the create new index assembly dialog is open. */
    isDialogOpen: boolean;

    /** List of linked partial models. */
    confirmNewIndexPartialModels: SlicePropsWithData<string>;

    /** Id of the selected assembly. */
    selectedAssemblyId: string;

    /** Success of the new index creation of the assembly. */
    newIndexAssemblySuccess: boolean;
}

/**
 * Create the redux slice.
 */
export const projectNewAssemblyIndexSlice = createSlice({
    name: 'projectNewAssemblyIndex',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    openNewAssemblyIndexDialog,
    closeNewAssemblyIndexDialog,
    setSelectedAssemblyId,
    resetNewIndexAssemblySuccess,
    clearNewIndexPartialModels,
} = projectNewAssemblyIndexSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectNewAssemblyIndexSlice.reducer;
